import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useCapabilities } from "@web/auth/capabilities";
import { useSession } from "@web/auth/session";

// Route-level authorization guard for the operator console. Renders its children only for a session
// holding `platformAdmin` (PLATFORM_ADMIN); otherwise it says so.
//
// It does NOT reuse @web/app/RequireRole, which bounces a caller to "/". That is right in the product,
// where "/" is the learner's own page and every session may see it, and wrong here: "/" on this host
// redirects to the tenant list, which is gated on the very capability that just failed, so a bounce
// would be a redirect loop rather than a refusal. Every route of this app needs the same one
// capability, so there is nowhere to send a caller who lacks it — the honest answer is to tell them,
// and to offer the only action that changes anything, signing out and in as somebody else.
//
// Like every client-side check in this workspace it only shapes the UI. The API enforces its own
// authority on every `/api/platform/` call — since task C-2 that gate checks the role AND that the call
// arrived on this entry's own Keycloak client (`ApiGuards.RequirePlatformSurface`, driven over every
// mapped route by Clms.PlatformSurfaceGuard.Tests) — so a hand-crafted request gets nothing from
// bypassing this component, and neither does a request from the product's own origin.
export function RequirePlatformAdmin({ children }: { children: ReactNode }) {
  const { can } = useCapabilities();
  const { t } = useTranslation();
  const { signOut } = useSession();

  if (!can("platformAdmin")) {
    return (
      <section className="not-found">
        <p role="alert" className="banner banner--error" data-testid="platform-access-denied">
          {t("platform.accessDenied")}
        </p>
        <button type="button" className="btn btn--secondary" onClick={signOut}>
          {t("auth.signOut")}
        </button>
      </section>
    );
  }
  return <>{children}</>;
}
